import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Plus,
  Loader,
  Wrench,
  Tag,
  Layers,
  ShoppingBag,
  Wallet,
  Sparkles,
  TrendingUp,
  Boxes,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import AddCompositionModal from "../components/AddCompositionModal";
import CompositionFormModal from "../components/CompositionFormModal";
import AddServiceModal from "../components/AddServiceModal";
import ConfirmationModal from "../components/ConfirmationModal";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isAddCompositionModalOpen, setIsAddCompositionModalOpen] =
    useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [compositionFormType, setCompositionFormType] = useState(null);
  const [selectedComposition, setSelectedComposition] = useState(null);
  const [compositionToDelete, setCompositionToDelete] = useState(null);

  const [serviceData, setServiceData] = useState(null);
  const [compositionData, setCompositionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const extractServiceData = (response) => response?.data || response || null;

  const extractCompositionData = (response) => {
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const refreshServiceAndCompositions = async () => {
    const [serviceResponse, compositionResponse] = await Promise.all([
      masterService.services.get(id),
      masterService.services.compositions.list(id),
    ]);
    setServiceData(extractServiceData(serviceResponse));
    setCompositionData(extractCompositionData(compositionResponse));
  };

  useEffect(() => {
    const fetchServiceDetail = async () => {
      try {
        setLoading(true);
        await refreshServiceAndCompositions();
      } catch (err) {
        console.error("Error fetching service detail:", err);
        setError(err.message || "Failed to fetch service detail");
      } finally {
        setLoading(false);
      }
    };
    if (!id) return;
    fetchServiceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val || 0);

  const handleSelectCompositionType = (type) => {
    setIsAddCompositionModalOpen(false);
    setSelectedComposition(null);
    setCompositionFormType(type);
  };

  const handleSaveComposition = async (payload) => {
    try {
      if (selectedComposition) {
        await masterService.services.compositions.update(
          id,
          selectedComposition.id,
          payload,
        );
      } else {
        await masterService.services.compositions.create(id, payload);
      }
      await refreshServiceAndCompositions();
      setCompositionFormType(null);
      setSelectedComposition(null);
    } catch (err) {
      console.error("Error saving composition:", err);
      throw new Error(err.message || "Failed to save composition");
    }
  };

  const handleEditComposition = (composition) => {
    setSelectedComposition(composition);
    setCompositionFormType(composition.type || "product");
  };

  const handleDeleteComposition = async (composition) => {
    setCompositionToDelete(composition);
  };

  const confirmDeleteComposition = async () => {
    if (!compositionToDelete) return;
    try {
      await masterService.services.compositions.delete(
        id,
        compositionToDelete.id,
      );
      await refreshServiceAndCompositions();
      showSuccess("Komposisi berhasil dihapus");
    } catch (err) {
      console.error("Error deleting composition:", err);
      showError(err.message || "Gagal menghapus komposisi");
    } finally {
      setCompositionToDelete(null);
    }
  };

  const handleEditService = () => setIsEditServiceModalOpen(true);

  const handleSaveService = async (payload) => {
    try {
      await masterService.services.update(id, payload);
      const response = await masterService.services.get(id);
      setServiceData(extractServiceData(response));
      showSuccess("Layanan berhasil diperbarui");
      setIsEditServiceModalOpen(false);
    } catch (err) {
      console.error("Error updating service:", err);
      throw err;
    }
  };

  const sidebarToggle = () =>
    setIsSidebarCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
      return newState;
    });

  const totalBuyPrice = compositionData.reduce((sum, item) => {
    const itemTotal = Number(
      item.total_buy_price ?? item.total_sell_price ?? item.total_price ?? 0,
    );
    return sum + (Number.isNaN(itemTotal) ? 0 : itemTotal);
  }, 0);

  const marginPercentage =
    serviceData?.sell_price && totalBuyPrice > 0
      ? Math.round(
          ((serviceData.sell_price - totalBuyPrice) / serviceData.sell_price) *
            100,
        )
      : 0;

  // === Loading ===
  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={sidebarToggle}
          activePage="master-data"
        />
        <div
          className={`flex-1 flex items-center justify-center transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-teal-100" />
              <div className="absolute inset-0 rounded-full border-4 border-teal-600 border-t-transparent animate-spin" />
            </div>
            <p className="text-slate-600 font-medium">
              Memuat detail layanan...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // === Error ===
  if (error || !serviceData) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={sidebarToggle}
          activePage="master-data"
        />
        <div
          className={`flex-1 flex items-center justify-center transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-rose-50 ring-1 ring-rose-100 flex items-center justify-center mx-auto mb-4">
              <Wrench className="w-8 h-8 text-rose-400" />
            </div>
            <p className="text-slate-700 font-semibold mb-3">
              {error || "Layanan tidak ditemukan"}
            </p>
            <button
              onClick={() => navigate("/master-data/layanan")}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 shadow-sm shadow-teal-600/20 transition-colors text-sm font-medium">
              Kembali ke Layanan
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isActive = serviceData.status === "ACTIVE";

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        activePage="master-data"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-6 lg:p-8 max-w-full">
          {/* === Hero Header === */}
          <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 rounded-3xl p-6 lg:p-8 mb-6 overflow-hidden shadow-lg shadow-teal-900/10">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-60 h-60 bg-emerald-300/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-stretch justify-between gap-5">
              <div className="flex items-stretch gap-4 min-w-0">
                <button
                  type="button"
                  onClick={() => navigate("/master-data/layanan")}
                  aria-label="Kembali"
                  title="Kembali"
                  className="self-stretch w-16 lg:w-20 rounded-2xl bg-white/10 hover:bg-white/20 ring-1 ring-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0">
                  <ArrowLeft className="w-8 h-8" />
                </button>
                <div className="min-w-0 self-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-100/90 mb-0.5">
                    Detail Layanan
                  </p>
                  <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight leading-tight truncate">
                    {serviceData.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {serviceData.code && (
                      <span className="inline-flex items-center gap-1 text-xs text-teal-50 bg-white/15 px-2.5 py-1 rounded-full ring-1 ring-white/25 font-medium">
                        <Tag className="w-3 h-3" /> {serviceData.code}
                      </span>
                    )}
                    {serviceData.category_name && (
                      <span className="inline-flex items-center gap-1 text-xs text-teal-50 bg-white/15 px-2.5 py-1 rounded-full ring-1 ring-white/25 font-medium">
                        <Layers className="w-3 h-3" />{" "}
                        {serviceData.category_name}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${
                        isActive
                          ? "bg-emerald-400/20 text-emerald-50 ring-emerald-300/40"
                          : "bg-rose-400/20 text-rose-50 ring-rose-300/40"
                      }`}>
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-300" : "bg-rose-300"}`}
                      />
                      {isActive ? "Aktif" : "Non-Aktif"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleEditService}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-teal-700 rounded-xl hover:bg-teal-50 font-semibold text-sm shadow-md shadow-teal-900/20 transition-colors">
                  <Pencil className="w-4 h-4" />
                  Ubah Layanan
                </button>
              </div>
            </div>
          </div>

          {/* === Stat Cards === */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<ShoppingBag className="w-5 h-5" />}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              label="Stok Tersedia"
              value={
                <>
                  {serviceData.stock !== undefined ? serviceData.stock : 0}{" "}
                  <span className="text-sm font-normal text-slate-400 uppercase">
                    {serviceData.unit_name || "-"}
                  </span>
                </>
              }
              hint="Berdasarkan komposisi"
            />
            <StatCard
              icon={<Wallet className="w-5 h-5" />}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="Total Harga Beli"
              value={formatCurrency(totalBuyPrice)}
              hint="Akumulasi komposisi"
            />
            <StatCard
              icon={<Sparkles className="w-5 h-5" />}
              iconBg="bg-teal-50"
              iconColor="text-teal-600"
              label="Harga Jual"
              value={formatCurrency(serviceData.sell_price)}
              hint="Harga jual layanan"
              accent
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5" />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              label="Margin"
              value={
                <span
                  className={
                    marginPercentage >= 0 ? "text-emerald-700" : "text-rose-600"
                  }>
                  {marginPercentage}%
                </span>
              }
              hint="Profit dari komposisi"
            />
          </div>

          {/* === Composition Section === */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                  <Boxes className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Komposisi Layanan
                  </h2>
                  <p className="text-xs text-slate-500">
                    {compositionData.length > 0
                      ? `${compositionData.length} komposisi · Total ${formatCurrency(totalBuyPrice)}`
                      : "Belum ada komposisi"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAddCompositionModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors text-sm shadow-sm shadow-teal-600/20">
                <Plus className="w-4 h-4" />
                Tambah Komposisi
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-3" />
                <p className="text-sm text-slate-500">
                  Memuat komposisi layanan...
                </p>
              </div>
            ) : compositionData.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-4">
                  <Boxes className="w-8 h-8" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  Belum Ada Komposisi
                </h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                  Tambahkan komposisi produk atau jasa untuk menghitung modal dan
                  mengurangi stok bahan baku saat layanan terjual.
                </p>
                <button
                  onClick={() => setIsAddCompositionModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors text-sm shadow-sm shadow-teal-600/20">
                  <Plus className="w-4 h-4" />
                  Tambah Komposisi
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-24">
                        Aksi
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Tipe
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Nama Komposisi
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Satuan
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Harga Satuan / Tarif
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Digunakan
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Total Biaya Modal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {compositionData.map((item) => {
                      const isItemProduct = !!(
                        item.product_id || item.type === "product"
                      );
                      const unitPrice =
                        item.sell_price || item.unit_price || 0;
                      const usedQty = item.used || item.quantity || 1;
                      const totalPrice =
                        item.total_sell_price ||
                        item.total_buy_price ||
                        item.total_price ||
                        unitPrice * usedQty;

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-slate-50/70 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleEditComposition(item)}
                                className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                                title="Edit">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteComposition(item)}
                                className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                                title="Hapus">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-xs font-semibold">
                            {isItemProduct ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                                Produk (Potong Stok)
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                                Jasa (Tanpa Stok)
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">
                            {item.product_name ||
                              item.service_name ||
                              item.name ||
                              "-"}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700">
                            {item.unit_name || item.unit || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-900">
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                            {usedQty}{" "}
                            <span className="text-xs text-slate-500 font-normal">
                              {item.unit_name || item.unit || ""}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-900 font-semibold">
                            {formatCurrency(totalPrice)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50/60 border-t border-slate-200">
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Total Modal Komposisi
                      </td>
                      <td className="px-6 py-3 text-sm font-bold text-teal-700">
                        {formatCurrency(totalBuyPrice)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddCompositionModal
        isOpen={isAddCompositionModalOpen}
        onClose={() => setIsAddCompositionModalOpen(false)}
        onSelectType={handleSelectCompositionType}
      />

      <CompositionFormModal
        isOpen={!!compositionFormType}
        onClose={() => {
          setCompositionFormType(null);
          setSelectedComposition(null);
        }}
        type={compositionFormType}
        initialData={selectedComposition}
        onSave={handleSaveComposition}
        serviceId={id}
      />

      <AddServiceModal
        isOpen={isEditServiceModalOpen}
        onClose={() => setIsEditServiceModalOpen(false)}
        initialData={serviceData}
        onSave={handleSaveService}
      />

      <ConfirmationModal
        isOpen={!!compositionToDelete}
        onClose={() => setCompositionToDelete(null)}
        onConfirm={confirmDeleteComposition}
        title="Hapus Komposisi?"
        message={`Apakah anda yakin ingin menghapus komposisi "${
          compositionToDelete?.product_name ||
          compositionToDelete?.service_name ||
          compositionToDelete?.name ||
          "-"
        }"?`}
        confirmText="Hapus"
        type="danger"
      />
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  hint,
  accent = false,
}) {
  return (
    <div
      className={`relative bg-white p-5 rounded-2xl ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:ring-slate-300/60 transition-all overflow-hidden ${
        accent
          ? "ring-teal-300/70 bg-gradient-to-br from-white to-teal-50/40"
          : ""
      }`}>
      <div className="flex items-start justify-between mb-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg} ${iconColor}`}>
          {icon}
        </div>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      <p className="text-xl font-bold text-slate-900 leading-tight">{value}</p>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
